import { useContext, useMemo } from 'react';

import { ApolloError, useMutation } from '@apollo/client';
import { IContextMenuItem } from '@smartshell/ui-kit';
import { useNavigate } from 'react-router-dom';

import { LocalizationContext } from 'localization';
import {
  COMPANY_NEWS_LIST,
  DELETE_COMPANIES_NEWS,
  ICompanyNewsList,
  IDeleteCompanyNewsRequest,
  IUpdateCompanyNewsRequest,
  UPDATE_COMPANIES_NEWS,
} from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';

import s from '../News.module.scss';

export const useContextMenuItems = (): IContextMenuItem<ICompanyNewsList>[] => {
  const navigate = useNavigate();

  const [deleteNews, { loading: deleteLoading }] =
    useMutation<IDeleteCompanyNewsRequest>(DELETE_COMPANIES_NEWS, {
      fetchPolicy: 'no-cache',
      refetchQueries: [COMPANY_NEWS_LIST],
      onCompleted: () => {
        notification.info({
          t,
          type: 'success',
          title: t('delete.new.notification.success'),
          dismiss: 5,
        });
      },
      onError: (error: ApolloError) => {
        notification.info({
          t,
          type: 'error',
          title: t('delete.new.notification.error'),
          message: error.message,
          dismiss: 5,
        });
      },
    });

  const [updateNews] = useMutation<IUpdateCompanyNewsRequest>(
    UPDATE_COMPANIES_NEWS,
    {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        notification.info({
          t,
          type: 'success',
          title: t('update.new.notification.success'),
          dismiss: 5,
        });
      },
      onError: (error: ApolloError) => {
        notification.info({
          t,
          type: 'error',
          title: t('update.new.notification.error'),
          message: error.message,
          dismiss: 5,
        });
      },
    }
  );

  const { t } = useContext(LocalizationContext);

  return useMemo(
    () => [
      {
        key: 'edit',
        text: t('edit'),
        iconName: 'EditIcon',
        onClick: (value) => navigate(`/news/${value.id}`),
        isLastInGroup: true,
      },
      {
        key: 'delete',
        text: t('delete'),
        iconName: 'BucketIcon',
        className: s.context_remove,
        onClick: (value) => {
          openConfirmDialog(t, 'remove', {
            title: t('modal.delete.news.title'),
            message: t('modal.delete.news.description', value.title),
            loading: deleteLoading,
          }).then(({ status }) => {
            if (status === 'ACCEPTED') {
              return deleteNews({
                variables: { deleteCompanyNewsId: value.id },
              });
            }
          });
        },
      },
    ],
    [t, updateNews, deleteNews]
  );
};
